'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';

const products = [
    {
        id: 'wisly',
        name: 'Wisly',
        category: 'Education Technology',
        tagline: 'Reimagining education through a connected ecosystem.',
        description:
            'Wisly connects students, parents, tutors, and institutions on a single intelligent platform — making education more accessible, trackable, and effective for everyone involved.',
        accentColor: '#3757ff',
        accentBg: 'rgba(55, 88, 255, 0.14)',
        features: [
            'Student progress tracking in real time',
            'Parent-tutor communication hub',
            'Institutional dashboard & analytics',
            'Adaptive learning pathways',
        ],
        image: 'https://images.unsplash.com/photo-1694532409273-b26e2ce266ea',
        imageAlt:
            'Students using tablets and laptops in a bright modern classroom, warm natural lighting, collaborative educational setting',
        href: '/products',
    },
    {
        id: 'capstate',
        name: 'CapState',
        category: 'Developer Tools',
        tagline: 'Rethinking version control for modern developers.',
        description:
            'CapState gives developers granular control over their codebase — restore exactly the function, block, or line you need from any point in history, without reverting entire files.',
        accentColor: '#E8FF47',
        accentBg: 'rgba(230, 255, 71, 0.14)',
        features: [
            'Granular code-block restoration',
            'Function-level history tracking',
            'Cross-branch state comparison',
            'Zero-friction team collaboration',
        ],
        image:
            'https://img.rocket.new/generatedImages/rocket_gen_img_178ed799d-1785088751486.png',
        imageAlt:
            'Dark code editor screen with colorful syntax highlighting, dim atmospheric lighting, deep shadows, professional developer workspace',
        href: '/products',
    },
];

export default function ProductEcosystemSection() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                }
            },
            { threshold: 0.15 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="py-24 px-6 md:px-10 border-t border-border opacity-100"
        >
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                    <div>
                        <span className="section-label text-primary mb-3 block">
                            Product Ecosystem
                        </span>

                        <h2 className="text-section-title text-foreground">
                            One company. Multiple ways
                            <br className="hidden md:block" /> to move forward.
                        </h2>
                    </div>

                    <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                        Each Codigo product is built to be exceptional in its own domain —
                        and designed to evolve with its users.
                    </p>
                </div>

                {/* Product Cards */}
                <div className="grid md:grid-cols-2 gap-6">
                    {products.map((product, index) => (
                        <div
                            key={product.id}
                            className={`product-card glass-card ${visible ? 'animate-fade-up' : 'opacity-0'
                                }`}
                            style={{
                                animationDelay: `${index * 150}ms`,
                            }}
                        >
                            {/* Image */}
                            <div className="relative h-56 overflow-hidden">
                                <AppImage
                                    src={product.image}
                                    alt={product.imageAlt}
                                    fill
                                    className="object-cover group-hover:scale-105 transition-transform duration-700"
                                />

                                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />

                                <div className="absolute top-4 left-4">
                                    <span
                                        className="section-label px-3 py-1.5 rounded-full text-xs font-700"
                                        style={{
                                            color: product.accentColor,
                                            background: product.accentBg,
                                        }}
                                    >
                                        {product.category}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-8">

                                {/* Product Name */}
                                {product.id === 'wisly' ? (
                                    <div className="flex items-center gap-1.5 mb-2">

                                        <AppLogo
                                            src='/assets/images/wisly/app_logo.png'
                                            size={32}
                                        />

                                        <h3 className="text-xl font-800 font-bold tracking-tight text-foreground">
                                            Wisly
                                        </h3>
                                    </div>
                                ) : product.id === 'capstate' ? (
                                    <h3 className="text-2xl font-bold font-800 tracking-tight mb-2">
                                        <span className="text-white">Cap</span>
                                        <span style={{ color: '#E8FF47' }}>State</span>
                                    </h3>
                                ) : (
                                    <h3 className="text-2xl font-800 tracking-tight text-foreground mb-2">
                                        {product.name}
                                    </h3>
                                )}

                                {/* Tagline */}
                                <p
                                    className="text-sm font-600 mb-3"
                                    style={{ color: product.accentColor }}
                                >
                                    {product.tagline}
                                </p>

                                {/* Description */}
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    {product.description}
                                </p>

                                {/* Features */}
                                <ul className="space-y-2.5 mb-8">
                                    {product.features.map((feature) => (
                                        <li
                                            key={feature}
                                            className="flex items-start gap-3 text-sm text-muted-foreground"
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                className="mt-0.5 shrink-0"
                                            >
                                                <path
                                                    d="M3 8l3.5 3.5L13 4"
                                                    stroke={product.accentColor}
                                                    strokeWidth="1.75"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>

                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {/* Learn More */}
                                <Link
                                    href={product.href}
                                    className="link-arrow text-sm font-700 text-foreground hover:opacity-80 transition-opacity"
                                >
                                    Learn more about {product.name}

                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M3 8h10M9 4l4 4-4 4"
                                            stroke="currentColor"
                                            strokeWidth="1.75"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}